import useDialog from 'hooks/useDialog';
import useSiteMutations from 'FeatureModules/Site/hooks/useSiteMutations';
import { useNavigate } from 'react-router-dom';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { ESiteType, IBaseFarmSite, IBaseSite } from 'FeatureModules/Site/types/site.types';

const useSiteActions = () => {
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const { deleteSite } = useSiteMutations();

  const handleCreateSite = ({
    ownedBy,
    siteClusterId,
    defaultSiteType,
    defaultRawMaterialId,
    defaultCountry,
  }: {
    ownedBy?: IBasePartnerCompany;
    siteClusterId?: string;
    defaultSiteType?: ESiteType;
    defaultRawMaterialId?: string;
    defaultCountry?: string;
  } = {}) => {
    openDialog({
      type: 'ADD_EDIT_SITE',
      // In the client view internal sites we prefill the company with your own company
      props: {
        ownedBy,
        siteClusterId,
        defaultSiteType,
        defaultRawMaterialId,
        defaultCountry,
      },
    });
  };

  const handleCreateSiteCluster = () => {
    openDialog({
      type: 'ADD_EDIT_SITE_CLUSTER',
    });
  };

  const handleEditSite = (site: IBaseSite) => {
    if (site.siteType === ESiteType.CLUSTER) {
      openDialog({
        type: 'ADD_EDIT_SITE_CLUSTER',
        props: {
          siteCluster: site,
        },
      });
    } else {
      openDialog({
        type: 'ADD_EDIT_SITE',
        props: {
          site,
        },
      });
    }
  };

  const handleDeleteSite = (site: IBaseSite) => {
    if (site.siteType === ESiteType.CLUSTER) {
      openDialog({
        type: 'DELETE_SITE_CLUSTER',
        props: {
          siteCluster: site,
        },
      });
    } else {
      openDialog({
        type: 'ALERT',
        props: {
          title: 'Delete site',
          text: 'Are you sure you want to delete this site? All data will be lost and you will not be able to recover this item.',
          submitText: 'Delete',
          itemTitle: site.title,
          displayCloseButton: true,
          onSubmit: () => {
            deleteSite({ variables: { id: site.id } });
          },
          onCancel: () => undefined,
        },
      });
    }
  };

  const handleEditFarmPlots = (farm: IBaseFarmSite, defaultView: 'json' | 'map' = 'map') => {
    openDialog({
      type: 'ADD_EDIT_FARM_PLOTS',
      props: {
        farm,
        defaultView,
      },
    });
  };

  const handleManageSite = (site: IBaseSite) => {
    if (site.siteType === ESiteType.CLUSTER) {
      openDialog({
        type: 'ADD_MANAGE_CLUSTER_DECISION',
        props: { siteCluster: site, partner: site?.ownedBy },
      });
    } else {
      openDialog({
        type: 'MANAGE_SITE',
        props: {
          site,
        },
      });
    }
  };

  const handleSiteClick = (siteId: string) => navigate(`/sites/${siteId}`);

  return {
    handleCreateSite,
    handleEditSite,
    handleDeleteSite,
    handleCreateSiteCluster,
    handleEditFarmPlots,
    handleManageSite,
    handleSiteClick,
  };
};

export default useSiteActions;
